import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { MiDetailFields } from "@miplatform/components";
import { BUTTON_LABELS, INPUT_TYPES, PERMISSIONS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IInspectionAuthorityResponse } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useInspectionAuthorities, usePermissions } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { INSPECTION_AUTHORITY_TYPES } from "../InspectionAuthorities.constants";


const schema = z.object({
  name: z.string().min(1, "Name is required"),
  url: z.string().min(1, "URL is required"),
  type: z.string().min(1, "Type is required"),
});

type IInspectionAuthorityFormData = z.infer<typeof schema>;

const defaultValues: IInspectionAuthorityFormData = {
  name: "",
  url: "",
  type: "City",
};

export function ConfigInspectionAuthorityDetails() {
  const { goBack, params } = useRouter();
  const { id } = params;

  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission(PERMISSIONS.OPERATIONS.ADMINISTRATION);

  const {
    createInspectionAuthority,
    updateInspectionAuthority,
    getInspectionAuthorityById,
    isLoading,
  } = useInspectionAuthorities();

  const {
    control,
    formState: { isValid, isDirty },
    getValues,
    reset,
    trigger,
  } = useForm<IInspectionAuthorityFormData>({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(schema),
  });

  const isCreating = !id;
  const watchName = useWatch({ control, name: "name" });

  const handleSave = async () => {
    const data = getValues();
    if (id) {
      const response = await updateInspectionAuthority({
        data: { ...data, id },
      });
      if (response?.data) {
        reset({
          ...response.data,
          type: String(response.data.type ?? "City"),
        });
      }
    } else {
      await createInspectionAuthority({ data });
    }
  };

  const canSave = !isLoading && !isReadOnly && isValid && isDirty;

  const pageActions = isReadOnly
    ? null
    : {
        cancel: {
          label: BUTTON_LABELS.CANCEL,
          onClick: () => {
            reset();
            goBack();
          },
        },
        submit: {
          label: "Save Inspection Authority",
          onClick: handleSave,
          disabled: !canSave,
        },
      };

  const renderPageTitle = () => {
    const action = isCreating ? "Create Inspection Authority" : "Edit Inspection Authority";
    const nameLabel = watchName || "Inspection Authority";
    return (
      <Typography variant="HD4">
        {action}:{" "}
        <Typography variant="HD4" color="primary" component="span">
          {nameLabel}
        </Typography>
      </Typography>
    );
  };

  const detailFields = useMemo(
    () => [
      {
        formControl: control,
        label: "Inspection Authority",
        name: "name",
        width: "50%",
        required: true,
      },
      {
        formControl: control,
        label: "Inspection Authority Type",
        name: "type",
        width: "50%",
        required: true,
        fieldType: INPUT_TYPES.SELECT,
        options: INSPECTION_AUTHORITY_TYPES,
        shrink: true,
      },
      {
        formControl: control,
        label: "Inspection Portal URL",
        name: "url",
        width: "100%",
        required: true,
      },
    ],
    [control]
  );

  useEffect(() => {
    if (!id) return;
    const loadAuthority = async () => {
      const response: { data?: IInspectionAuthorityResponse } =
        await getInspectionAuthorityById(id);
      if (response?.data) {
        reset({
          ...response.data,
          url: response.data.url ?? "",
          type: String(response.data.type ?? "City"),
        });
        await trigger();
      }
    };
    loadAuthority().catch(() => goBack());
  }, [id]);

  return (
    <ConfigPageLayout
      renderTitle={renderPageTitle}
      bottomActions={pageActions}
      loading={isLoading}
    >
      <Box display="flex" flexDirection="column" gap={2} p={4} height="100%">
        <ConfigAccordionHeader title="Inspection Authority Details" disableCollapse>
          <MiDetailFields detailFields={detailFields} />
        </ConfigAccordionHeader>
      </Box>
    </ConfigPageLayout>
  );
}
